import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useToast } from '@medely/ui-kit';
import { useNetworkRequest } from '../useNetworkRequest';
import { useProfessionalPoolContext } from '@mp/hooks/useProfessionalPoolContext';
import { PoolMutationError } from './usePoolMutations';

const POOL_JOB_QUERY_KEYS = [
  'GetJobForJobDetailsContainer',
  'GetAvailableJobForAvailableJobDetailsContainer',
  'GetProfessionalPoolPqs',
  'GetProfessionalPoolOnboardingStatus',
  'GetMissingConditions',
  'job',
  'availableJob',
  'jobs',
  'professionalQualifications',
  'GetProfessionalTodos',
] as const;

const invalidatePoolJobQueries = (queryClient: QueryClient): void => {
  POOL_JOB_QUERY_KEYS.forEach((key) => {
    queryClient.invalidateQueries([key]);
  });
};

const toPoolMutationError = (error: unknown): PoolMutationError => {
  const err = error as
    | {
        response?: { status?: number; data?: { error?: string; message?: string } };
        message?: string;
      }
    | undefined;
  return {
    message: err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? '',
    status: err?.response?.status,
  };
};

const OPT_OUT_ERROR_FALLBACK = 'We could not opt you out from this pool. Please try again.';

export type UsePoolOptOutMutationResult = {
  optOut: () => Promise<unknown>;
  isOptingOut: boolean;
};

export const usePoolOptOutMutation = (poolId: number | undefined): UsePoolOptOutMutationResult => {
  const { axios } = useNetworkRequest();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { poolPqs } = useProfessionalPoolContext(poolId);
  const professionalQualificationId = poolPqs[0]?.id;

  const { mutateAsync: optOut, isLoading: isOptingOut } = useMutation(
    () => {
      if (!professionalQualificationId) {
        return Promise.reject(new Error(OPT_OUT_ERROR_FALLBACK));
      }
      return axios.post(`/v3/professional/pools/${poolId}/opt_out`);
    },
    {
      onError: (error) => {
        const poolMutationError = toPoolMutationError(error);
        toast.error(poolMutationError.message ?? OPT_OUT_ERROR_FALLBACK);
      },
      onSuccess: () => {
        invalidatePoolJobQueries(queryClient);
      },
    },
  );

  return { optOut, isOptingOut };
};
